import { useEffect, useRef } from 'react';

export const BINANCE_SYMBOL_BY_COIN_ID = {
  bitcoin: 'BTCUSDT',
  ethereum: 'ETHUSDT',
  binancecoin: 'BNBUSDT',
  solana: 'SOLUSDT',
  'pax-gold': 'PAXGUSDT',
  ripple: 'XRPUSDT',
  dogecoin: 'DOGEUSDT',
  cardano: 'ADAUSDT',
  'avalanche-2': 'AVAXUSDT',
};

const COIN_ID_BY_BINANCE_SYMBOL = Object.fromEntries(
  Object.entries(BINANCE_SYMBOL_BY_COIN_ID).map(([coinId, symbol]) => [symbol, coinId])
);

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export function normalizeBinanceTicker(payload) {
  const ticker = payload?.data || payload;
  const symbol = String(ticker?.s || '').toUpperCase();
  const coinId = COIN_ID_BY_BINANCE_SYMBOL[symbol];
  const currentPrice = toFiniteNumber(ticker?.c);
  const change = toFiniteNumber(ticker?.P);
  const high = toFiniteNumber(ticker?.h);
  const low = toFiniteNumber(ticker?.l);
  const updatedAt = toFiniteNumber(ticker?.E);

  if (!coinId || currentPrice === null || change === null || high === null || low === null) return null;

  return {
    coinId,
    current_price: currentPrice,
    price_change_percentage_24h: change,
    high_24h: high,
    low_24h: low,
    updatedAt: updatedAt ?? Date.now(),
  };
}

const streams = Object.values(BINANCE_SYMBOL_BY_COIN_ID)
  .map((symbol) => `${symbol.toLowerCase()}@ticker`)
  .join('/');
const tickerUrl = `wss://stream.binance.com:9443/stream?streams=${streams}`;
const reconnectDelays = [1000, 2000, 4000, 8000, 15000];

export default function useBinanceTicker({ enabled, onTicker }) {
  const onTickerRef = useRef(onTicker);

  useEffect(() => {
    onTickerRef.current = onTicker;
  }, [onTicker]);

  useEffect(() => {
    if (!enabled || typeof WebSocket !== 'function') return undefined;

    let disposed = false;
    let socket;
    let reconnectTimer;
    let retryCount = 0;

    const connect = () => {
      if (disposed) return;
      socket = new WebSocket(tickerUrl);
      socket.onopen = () => {
        retryCount = 0;
      };
      socket.onmessage = (event) => {
        try {
          const update = normalizeBinanceTicker(JSON.parse(event.data));
          if (update) onTickerRef.current?.(update);
        } catch {
          // Ignore malformed public-stream messages and keep the connection alive.
        }
      };
      socket.onclose = () => {
        if (disposed) return;
        const delay = reconnectDelays[Math.min(retryCount, reconnectDelays.length - 1)];
        retryCount += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [enabled]);
}
